import '@assets/styles/tiptap-preview.css';

import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import { articleApiClient } from '@src/api/company/article/articleApiClient';
import { articleFavoriteApiClient } from '@src/api/company/article/articleFavoriteApiClient';
import { bookmarkApiClient } from '@src/api/company/bookmark/bookmarkApiClient';
import { Article, ArticleFavorite } from '@src/api/types/article.ts';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useCategory } from '@src/context/CategoryContext';
import { ArticleStatus } from '@src/enum/articleStatus.ts';
import CustomError from '@src/exception/CustomError.ts';
import { useThrowAsyncError } from '@src/exception/useThrowAsyncError.ts';
import ArticleContent from '@src/features/shared/ArticleContent';
import { useBookmarkLoginPopup } from '@src/hooks/useBookmarkLoginPopup';
import { useScrollMobile } from '@src/hooks/useScrollMobile';
import { COMPANY_URL, ShowProps } from '@src/routes/company';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { generatePath, useParams } from 'react-router-dom';

function ArticleShow({ setFeature, setDescription }: ShowProps) {
  const { id } = useParams<{ id: string }>();
  const throwAsyncError = useThrowAsyncError();
  const [article, setArticle] = useState<Article | undefined>(undefined);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [favorite, setFavorite] = useState<ArticleFavorite | undefined>(undefined);
  const [displayRelatedArticles, setDisplayRelatedArticles] = useState<Article[]>([]);
  const { profile } = useCompanyAuth();
  const isLoggedIn = !!profile?.isLogin;
  const { menuMediaCategories } = useCategory();
  const { visiblePopupId, showPopupIfNotLoggedIn, hidePopup, popupRef } =
    useBookmarkLoginPopup();

  useEffect(() => {
    if (!id) {
      return;
    }

    window.scrollTo(0, 0);

    const fetchData = async () => {
      const res = await articleApiClient.show(Number(id));
      handleApiResponse(res, async (data) => {
        if (`${data.status}` !== ArticleStatus.PUBLISHED.toString()) {
          throwAsyncError(new CustomError('記事未公開', 404));
        }
        setArticle(data);
        setIsBookmarked(data.is_bookmarked ?? false);
        setFavorite(data.favorite);
        setDisplayRelatedArticles(data.related_articles as Article[]);
        setFeature(data.title);
        setDescription?.(data.description);
      });
    };

    fetchData();
  }, [id, throwAsyncError]);

  const handleBookmarkToggle = async () => {
    if (!article) {
      return;
    }
    setIsBookmarked((prev) => !prev);
    const res = await bookmarkApiClient.toggle(article.id);
    handleApiResponse(res, (data) => {
      setIsBookmarked(data.is_bookmarked);
    });
  };

  const handleFavoriteToggle = async () => {
    if (!article) {
      return;
    }

    const prevFavorite = favorite;
    const currentFavorite = favorite ?? { is_favorite: false, favorites_count: 0 };
    setFavorite({
      is_favorite: !currentFavorite.is_favorite,
      favorites_count: currentFavorite.is_favorite
        ? currentFavorite.favorites_count - 1
        : currentFavorite.favorites_count + 1,
    });

    const res = await articleFavoriteApiClient.toggle(article.id);
    handleApiResponse(res, (data) => {
      setFavorite(data);
    });
    if (res.result !== 'success' && res.result !== 'aborted') {
      setFavorite(prevFavorite);
    }
  };

  const handleRelatedArticleBookmarkToggle = async (articleId: number) => {
    setDisplayRelatedArticles((prev) =>
      prev.map((a) => (a.id === articleId ? { ...a, is_bookmarked: !a.is_bookmarked } : a)),
    );
    const res = await bookmarkApiClient.toggle(articleId);
    handleApiResponse(res, (data) => {
      setDisplayRelatedArticles((prev) =>
        prev.map((a) => (a.id === articleId ? { ...a, is_bookmarked: data.is_bookmarked } : a)),
      );
    });
  };

  const handleRelatedArticleFavoriteToggle = async (articleId: number) => {
    const prevArticles = displayRelatedArticles;
    setDisplayRelatedArticles((prev) =>
      prev.map((a) => {
        if (a.id !== articleId) {
          return a;
        }
        const currentFavorite = a.favorite ?? { is_favorite: false, favorites_count: 0 };
        return {
          ...a,
          favorite: {
            is_favorite: !currentFavorite.is_favorite,
            favorites_count: currentFavorite.is_favorite
              ? currentFavorite.favorites_count - 1
              : currentFavorite.favorites_count + 1,
          },
        };
      }),
    );
    const res = await articleFavoriteApiClient.toggle(articleId);
    handleApiResponse(res, (data) => {
      setDisplayRelatedArticles((prev) =>
        prev.map((a) => (a.id === articleId ? { ...a, favorite: data } : a)),
      );
    });
    if (res.result !== 'success' && res.result !== 'aborted') {
      setDisplayRelatedArticles(prevArticles);
    }
  };

  const { renderScrollMobile } = useScrollMobile({
    items: menuMediaCategories,
  });

  const shareData = article
    ? {
        url: `${window.location.origin}${generatePath(COMPANY_URL.ARTICLES_SHOW, { id: article.id })}`,
        title: article.title,
        tags: article.tags ? article.tags.map((tag) => tag.name) : [],
      }
    : null;

  return (
    <main className='pb-16 pt-10 md:pt-4'>
      <Helmet>
        <meta
          name='description'
          content={article?.description}
        />
        <meta
          property='og:title'
          content={article?.title}
        />
        <meta
          property='og:description'
          content={article?.description}
        />
        {article?.thumbnail_url && (
          <meta
            property='og:image'
            content={article.thumbnail_url}
          />
        )}
      </Helmet>
      <div className='m-auto max-w-[740px] lg:px-6'>
        <div className='hidden lg:-order-1 lg:block'>
          <div className='ml-[-24px] w-[calc(100%+48px)] pl-[24px]'>{renderScrollMobile()}</div>
        </div>
        {article && (
          <ArticleContent
            article={article}
            relatedArticles={displayRelatedArticles}
            isLoggedIn={isLoggedIn}
            registrationUrl={COMPANY_URL.REGISTRATION}
            loginUrl={COMPANY_URL.LOGIN}
            shareData={shareData}
            isBookmarked={isBookmarked}
            isLiked={favorite?.is_favorite}
            likesCount={favorite?.favorites_count}
            onLikeToggle={
              isLoggedIn ? handleFavoriteToggle : () => showPopupIfNotLoggedIn(`like-${id}`)
            }
            likePopupId={!isLoggedIn ? `like-${id}` : undefined}
            onBookmarkToggle={isLoggedIn ? handleBookmarkToggle : undefined}
            onRelatedArticleBookmarkToggle={
              isLoggedIn ? handleRelatedArticleBookmarkToggle : undefined
            }
            onRelatedArticleFavoriteToggle={
              isLoggedIn
                ? handleRelatedArticleFavoriteToggle
                : (articleId) => showPopupIfNotLoggedIn(`related-favorite-${articleId}`)
            }
            onBookmarkClickWhenLoggedOut={
              !isLoggedIn ? () => showPopupIfNotLoggedIn(`show-${id}`) : undefined
            }
            onRelatedArticleBookmarkClickWhenLoggedOut={
              !isLoggedIn ? (popupId: string) => showPopupIfNotLoggedIn(popupId) : undefined
            }
            bookmarkPopupId={`show-${id}`}
            visiblePopupId={!isLoggedIn ? visiblePopupId : undefined}
            popupRef={!isLoggedIn ? popupRef : undefined}
            onPopupClose={!isLoggedIn ? hidePopup : undefined}
          />
        )}
      </div>
    </main>
  );
}

export default ArticleShow;
